import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Sparkles, Rocket, Calendar } from "lucide-react";
import { api } from "../lib/api";
import Loading from "../components/Loading";
import FavHeart from "../components/FavHeart";
import ShareButton from "../components/ShareButton";
import CommentButton from "../components/CommentButton";

const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

function TypeChip({ type, category }) {
  const isAstro = type === "astronomy";
  return (
    <span
      className={`sat-label border px-2 py-0.5 text-[9px] inline-flex items-center gap-1 ${
        isAstro ? "border-fuchsia-400/60 text-fuchsia-300" : "border-[color:var(--sat-cyan)]/50 sat-cyan"
      }`}
    >
      {isAstro ? <Sparkles size={9} /> : <Rocket size={9} />}
      {category || (isAstro ? "ASTRONOMY" : "SPACE")}
    </span>
  );
}

export default function HistoryAllPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all|space|astronomy

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          Array.from({ length: 12 }, (_, i) =>
            api.get(`/milestones?month=${i + 1}`).then(r => r.data.results || []).catch(() => [])
          )
        );
        if (!alive) return;
        const all = results.flat();
        // Sort by (month, then day if parseable)
        all.sort((a, b) => {
          if (a.month !== b.month) return a.month - b.month;
          const da = parseInt((a.display_date || "").match(/\d+/)?.[0] || 0, 10);
          const db = parseInt((b.display_date || "").match(/\d+/)?.[0] || 0, 10);
          return da - db;
        });
        setItems(all);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter(m => (m.type || "space") === filter);
  }, [items, filter]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const m of filtered) {
      if (!g.has(m.month)) g.set(m.month, []);
      g.get(m.month).push(m);
    }
    return g;
  }, [filtered]);

  const counts = {
    all: items.length,
    space: items.filter(m => (m.type || "space") === "space").length,
    astronomy: items.filter(m => m.type === "astronomy").length,
  };

  return (
    <div data-testid="page-history-all">
      <Link to="/history" className="sat-label inline-flex items-center gap-2 mb-4 hover:sat-cyan" data-testid="back-history">
        <ArrowLeft size={14} /> BACK TO MONTHS
      </Link>

      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan flex items-center gap-2"><Calendar size={12} /> DIRECTIVE // BROWSE ALL</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">HISTORY TIMELINE</h1>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">
          Every milestone across the calendar — {counts.space} space & {counts.astronomy} astronomy events.
        </p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="history-all-filters">
        {[
          { key: "all", label: `ALL · ${counts.all}` },
          { key: "space", label: `SPACE · ${counts.space}` },
          { key: "astronomy", label: `ASTRONOMY · ${counts.astronomy}` },
        ].map(f => (
          <button
            key={f.key}
            data-testid={`history-all-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`sat-btn !py-2 !px-3 ${filter === f.key ? "sat-btn-solid" : ""}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <Loading message="LOADING TIMELINE" />}

      {!loading && filtered.length === 0 && (
        <div className="sat-label text-center py-12" data-testid="history-all-empty">NO MILESTONES MATCH</div>
      )}

      {!loading && Array.from(grouped.entries()).map(([month, list]) => (
        <section key={month} className="mb-8" data-testid={`history-month-${month}`}>
          <div className="flex items-baseline gap-3 mb-3 border-b border-[color:var(--sat-border)] pb-2">
            <span className="font-display font-bold text-2xl sat-cyan">{MONTHS[month - 1]}</span>
            <span className="sat-label text-[color:var(--sat-text-dim)]">{list.length} MILESTONES</span>
          </div>
          <div className="space-y-3">
            {list.map(m => {
              const isAstro = m.type === "astronomy";
              return (
                <article
                  key={m.id}
                  data-testid={`history-all-card-${m.id}`}
                  data-milestone-type={m.type || "space"}
                  className={`sat-brackets sat-surface border p-4 md:p-5 flex flex-col md:flex-row gap-4 transition-colors ${
                    isAstro ? "border-fuchsia-400/30 hover:border-fuchsia-400" : "border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)]"
                  }`}
                >
                  {m.image_url && (
                    <div className="md:w-40 md:min-w-[10rem] h-32 md:h-32 bg-[color:var(--sat-surface-2)] overflow-hidden border border-[color:var(--sat-border)]">
                      <img src={m.image_url} alt="" onError={(e) => e.currentTarget.style.display = "none"} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <TypeChip type={m.type} category={m.category} />
                        <span className="font-mono text-[11px] text-[color:var(--sat-text-dim)]">{m.display_date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CommentButton targetKind="milestone" targetId={m.id} title={m.title} size={11} testid={`comment-all-${m.id}`} />
                        <ShareButton
                          url={typeof window !== "undefined" ? `${window.location.origin}/history?m=${m.month}` : ""}
                          title={m.title} size={11} testid={`share-all-${m.id}`} className="!p-1.5"
                        />
                        <FavHeart
                          kind="milestone" ref_id={m.id} title={m.title} image={m.image_url || null}
                          payload={{ month: m.month, category: m.category, type: m.type, display_date: m.display_date }}
                          size={12} testid={`fav-all-${m.id}`}
                        />
                      </div>
                    </div>
                    <h3 className="font-display font-bold text-lg mt-2">{m.title}</h3>
                    <p className="text-sm text-[color:var(--sat-text-dim)] mt-1.5 leading-relaxed">{m.description}</p>
                    {m.source_url && (
                      <a href={m.source_url} target="_blank" rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1.5 mt-2 sat-label hover:underline ${isAstro ? "text-fuchsia-300" : "sat-cyan"}`}>
                        <ExternalLink size={11} /> SOURCE
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
